import React from "react";
import { createTheme, MantineColorsTuple } from "@mantine/core";

/**
 * PulseWave colors adapted for Mantine theme
 * Based on the PulseWave design system from libs/ui/styling/
 */

const primary: MantineColorsTuple = [
  "#fdfce8",
  "#f9f7d1",
  "#f5f3a4",
  "#f1ee72",
  "#ebe93e", // Primary
  "#e5e321",
  "#d8d614",
  "#c2c00f",
  "#adab0b",
  "#999608",
];

const secondary: MantineColorsTuple = [
  "#f4e6ff",
  "#e0ccf5",
  "#c199eb",
  "#a166e0",
  "#8533d6",
  "#7500da", // Secondary
  "#6a00c7",
  "#5d00b0",
  "#50009b",
  "#430086",
];

const tertiary: MantineColorsTuple = [
  "#e6f4ff",
  "#cce5ff",
  "#99ccff",
  "#66b3ff",
  "#3399ff",
  "#588feb", // Tertiary
  "#0073e6",
  "#0066cc",
  "#0059b3",
  "#004d99",
];

const bg: MantineColorsTuple = [
  "#e8eaef",
  "#b6bdce",
  "#939db6",
  "#627195",
  "#445581",
  "#152b61", // Background
  "#132758",
  "#0f1f45",
  "#0c1835",
  "#091229",
];

const success: MantineColorsTuple = [
  "#e8faed",
  "#b7f1c8",
  "#95eaad",
  "#64e087",
  "#46da70",
  "#18d14c", // Success
  "#16be45",
  "#119436",
  "#0d732a",
  "#0a5820",
];

const warning: MantineColorsTuple = [
  "#fff2e8",
  "#fed7b8",
  "#fdc396",
  "#fca866",
  "#fc9749",
  "#fb7d1b", // Warning
  "#e47219",
  "#b25913",
  "#8a450f",
  "#69350b",
];

const error: MantineColorsTuple = [
  "#ffe7e8",
  "#ffb3b7",
  "#ff8f95",
  "#fe5c64",
  "#fe3c46",
  "#fe0b18", // Error
  "#e70a16",
  "#b40811",
  "#8c060d",
  "#6b050a",
];

const gray: MantineColorsTuple = [
  "#f1f2f4",
  "#d7d9dd",
  "#b9bcc3",
  "#989ca5",
  "#757a86",
  "#535965", // Gray
  "#3c414c",
  "#2c3039",
  "#1b1f26",
  "#12161b", // Darkest - used for dark mode input backgrounds
];

/**
 * Custom Mantine theme for Pulse widget platform
 */
export const pulseTheme = createTheme({
  primaryColor: "yellow",
  colors: {
    primary,
    secondary,
    tertiary,
    bg,
    success,
    warning,
    error,
    gray,
    dark: gray, // Reuse gray tuple for dark mode colors
  },
  fontFamily: "Lato, system-ui, -apple-system, sans-serif",
  headings: {
    fontFamily: "Open Sans, system-ui, -apple-system, sans-serif",
  },
  defaultRadius: "sm",
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
  },
  shadows: {
    xs: "0 1px 2px rgba(0, 0, 0, 0.05)",
    sm: "0 1px 3px rgba(0, 0, 0, 0.1)",
    md: "0 4px 6px rgba(0, 0, 0, 0.1)",
    lg: "0 10px 15px rgba(0, 0, 0, 0.1)",
    xl: "0 20px 25px rgba(0, 0, 0, 0.1)",
  },
  components: {
    Select: {
      defaultProps: {
        styles: {
          input: {
            backgroundColor:
              "light-dark(var(--mantine-color-bg-0), var(--mantine-color-dark-9))",
            "--input-bd-focus": "var(--mantine-color-bg-1)",
          } as React.CSSProperties,
          option: {
            color: "var(--mantine-color-text)",
          } as React.CSSProperties,
        },
      },
    },
    Autocomplete: {
      defaultProps: {
        styles: {
          input: {
            backgroundColor:
              "light-dark(var(--mantine-color-bg-0), var(--mantine-color-dark-9))",
            "--input-bd-focus": "var(--mantine-color-bg-1)",
          } as React.CSSProperties,
          option: {
            color: "var(--mantine-color-text)",
          } as React.CSSProperties,
        },
      },
    },
    NumberInput: {
      defaultProps: {
        styles: {
          input: {
            backgroundColor:
              "light-dark(var(--mantine-color-bg-0), var(--mantine-color-dark-9))",
            "--input-bd-focus": "var(--mantine-color-bg-1)",
          } as React.CSSProperties,
        },
      },
    },
    TextInput: {
      defaultProps: {
        styles: {
          input: {
            backgroundColor:
              "light-dark(var(--mantine-color-bg-0), var(--mantine-color-dark-9))",
            "--input-bd-focus": "var(--mantine-color-bg-1)",
          } as React.CSSProperties,
        },
      },
    },
    TimeInput: {
      defaultProps: {
        styles: {
          input: {
            backgroundColor:
              "light-dark(var(--mantine-color-bg-0), var(--mantine-color-dark-9))",
            "--input-bd-focus": "var(--mantine-color-bg-1)",
          } as React.CSSProperties,
        },
      },
    },
    DatePickerInput: {
      defaultProps: {
        styles: {
          input: {
            backgroundColor:
              "light-dark(var(--mantine-color-bg-0), var(--mantine-color-dark-9))",
            "--input-bd-focus": "var(--mantine-color-bg-1)",
          } as React.CSSProperties,
        },
      },
    },
    Checkbox: {
      defaultProps: {
        color: "tertiary.3",
      },
    },
  },
});
